import { useTable } from "@refinedev/core";
import { Link, useSearchParams } from "react-router";

import { Badge } from "@/components/Badge";
import {
  ErrorBox,
  FilterBar,
  Loading,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
} from "@/components/Page";
import { EmptyRow, Table, Td, Th, Tr } from "@/components/Table";
import { ORDER_STATUS_OPTIONS, formatDateTime, formatMoney, orderStatus } from "@/lib/format";
import { filterValue } from "@/lib/query";

interface OrderRow extends Record<string, unknown> {
  id: string;
  status: string;
  total_gross: number;
  currency: string;
  invoice_no: string | null;
  created_at: string;
  paid_at: string | null;
  buyer_email: string | null;
  items_label: string;
}

export function OrdersListPage() {
  // Przegląd linkuje tu z gotowym filtrem ("płatność wisi"), więc status
  // z adresu jest stanem początkowym tabeli.
  const [searchParams] = useSearchParams();
  const statusZAdresu = searchParams.get("status") ?? "";

  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<OrderRow>({
      resource: "orders",
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
      filters: {
        initial: statusZAdresu
          ? [{ field: "status", operator: "eq", value: statusZAdresu }]
          : undefined,
      },
    });

  const ustawFiltr = (field: string, value: string) =>
    setFilters([{ field, operator: "eq", value }], "merge");

  return (
    <>
      <PageHeader
        title="Zamówienia"
        description="Status każdego zakupu, od złożenia po wysyłkę."
      />

      <FilterBar>
        <Select
          label="Status"
          value={filterValue(filters, "status")}
          options={ORDER_STATUS_OPTIONS}
          onChange={(value) => ustawFiltr("status", value)}
        />
        <SearchInput
          label="Szukaj"
          placeholder="adres kupującego, numer faktury"
          value={filterValue(filters, "query")}
          onChange={(value) => ustawFiltr("query", value)}
        />
      </FilterBar>

      <ErrorBox error={tableQuery.error} />

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Złożone</Th>
                <Th>Kupujący</Th>
                <Th>Pozycje</Th>
                <Th align="right">Kwota</Th>
                <Th>Status</Th>
                <Th>Faktura</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={6}>Żadne zamówienie nie pasuje do tych filtrów.</EmptyRow>
              )}
              {result.data.map((order) => {
                const status = orderStatus(order.status);
                return (
                  <Tr key={order.id}>
                    <Td nowrap>
                      <Link
                        to={`/zamowienia/${order.id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        {formatDateTime(order.created_at)}
                      </Link>
                    </Td>
                    <Td>{order.buyer_email ?? "-"}</Td>
                    <Td>{order.items_label || "-"}</Td>
                    <Td align="right" numeric nowrap>
                      {formatMoney(order.total_gross, order.currency)}
                    </Td>
                    <Td>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </Td>
                    <Td>{order.invoice_no ?? "-"}</Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
